import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import { Outlet } from 'react-router-dom';

import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { LegalGate } from './legal/LegalGate';

/**
 * App shell. Two regions:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │  TopBar (dark mode toggle + profile menu)    │
 *   ├──────────────┬───────────────────────────────┤
 *   │              │                               │
 *   │  Sidebar     │  <Outlet />                   │
 *   │  (nav)       │                               │
 *   │              │                               │
 *   └──────────────┴───────────────────────────────┘
 *
 * The Sidebar collapses to a temporary drawer on mobile widths so
 * the layout works on narrow viewports.
 */

// Narrow icon-forward rail (icons stacked on top of their labels), matching
// the Version D mockup.
const DRAWER_WIDTH = 140;

export function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleDrawer = () => setMobileOpen((prev) => !prev);

  return (
    // Fixed-viewport-height app shell. The outer box pins to the
    // viewport so the sidebar stays put while the main content area
    // scrolls within itself — without this, pages that exceed the
    // viewport (e.g. Profile with the Professional-details +
    // Mailing-address sections expanded) get cut off because no
    // ancestor container is set up to scroll. `100dvh` over `100vh`
    // so iOS Safari's collapsing toolbar doesn't trim the bottom.
    <Box sx={{ display: 'flex', height: '100dvh' }}>
      <TopBar
        leftSlot={
          <IconButton
            color="inherit"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2, display: { md: 'none' } }}
            aria-label="Open navigation"
          >
            <MenuIcon />
          </IconButton>
        }
      />

      <Sidebar
        width={DRAWER_WIDTH}
        mobileOpen={mobileOpen}
        onMobileClose={toggleDrawer}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
          height: '100dvh',
          overflowY: 'auto',
        }}
      >
        {/* Spacer that matches the TopBar's height so content starts
            below it. MUI's Toolbar at default density is the canonical
            way to do this without hardcoding pixel heights. */}
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
        <LegalGate />
      </Box>
    </Box>
  );
}
